import React, { useEffect, useState } from 'react';
import { Header, Icon, Divider, Button, Table, Modal, Menu } from 'semantic-ui-react';
import FilterForm from './FilterForm';
import request from 'services/http';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import AnimatedModal from 'components/AnimatedModal';
import { paramsToSearchURL } from 'utils';

export default function TrackerReport(props) {
    const [funcionarios, setFuncionarios] = useState(null);
    const [logs, setLogs] = useState(null);
    const [search, setSearch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalLog, setModalLog] = useState(null);

    useEffect(() => {
        request({
            method: 'GET',
            url: '/tracker-report/funcionarios',
            inheritError: true,
        })
            .toPromise()
            .then(({ response: { users } }) => setFuncionarios(users))
            .catch(setFuncionarios([]));
    }, []);

    useEffect(() => {
        if (props.location.search === '' && logs) {
            // This is a clear
            setLogs(null);
        }

        if (
            props.location.search !== '' &&
            (!search || search !== props.location.search) &&
            !loading
        ) {
            setLoading(true);
            request({
                method: 'GET',
                url: `/tracker-report/logs${props.location.search}`,
                inheritError: true,
            })
                .toPromise()
                .then(({ response: { logs, count, pageSize, pageNum } }) =>
                    setLogs({ dados: logs, count, pageSize, pageNum }),
                )
                .catch((error) => {
                    if (error.response.message === 'Validation error') {
                        toast.error(
                            Object.values(error.response.payload)
                                .map((it) => it.msg)
                                .join('\n'),
                        );
                    } else toast.error(error.response.message);
                    setLogs([]);
                })
                .finally(() => {
                    setLoading(() => {
                        setSearch(props.location.search);
                        return false;
                    });
                });
        }
    }, [props.location.search, logs, search, loading]);

    function onSubmit(search, page = 1) {
        const data = Object.fromEntries(new URLSearchParams(search.replace('?=', '')).entries());

        if ('page' in data) {
            delete data['page'];
        }
        setLoading(true);
        request({
            method: 'GET',
            url: `/tracker-report/logs?${paramsToSearchURL(data, false)}&page=${page}`,
            inheritError: true,
        })
            .toPromise()
            .then(({ response: { logs, count, pageSize, pageNum } }) =>
                setLogs({ dados: logs, count, pageSize, pageNum }),
            )
            .catch((error) => {
                if (error.response.message === 'Validation error') {
                    toast.error(
                        Object.values(error.response.payload)
                            .map((it) => it.msg)
                            .join('\n'),
                    );
                } else toast.error(error.response.message);
                setLogs([]);
            })
            .finally(() => {
                setLoading(() => {
                    setSearch(props.location.search);
                    return false;
                });
            });
    }

    function formatEventToTable(log) {
        switch (log.tracking.action) {
            case 'PageLoaded':
                return 'Acesso a página';
            case 'Click':
                return `Clique em botão "${log.tracking.element.text}"`;
            case 'Submit':
                return (
                    <div>
                        Envio de formulário
                        <Button
                            icon="plus"
                            size="tiny"
                            compact
                            onClick={() => setModalLog(log)}
                            className="btn btn-primary ml-2"
                            content="Detalhes"
                        />
                    </div>
                );
            default:
                return '---';
        }
    }

    const formProps = {
        onSubmit,
        funcionarios,
        search,
        location: props.location,
    };

    let pages = Math.floor(logs?.count / logs?.pageSize);
    pages += logs?.count % logs?.pageSize > 0 ? 1 : 0;

    return (
        <>
            <div className="row mx-0">
                <div className="col px-0">
                    <Header as="h3" className="mt-1">
                        <Icon.Group size="big">
                            <Icon name="tasks" className="primary-color" />
                        </Icon.Group>
                        <Header.Content className="primary-color">
                            Rastreio de Atividades
                            <Header.Subheader className="secondary-color">
                                <strong>Utilização dos sistemas por funcionário</strong>
                            </Header.Subheader>
                        </Header.Content>
                    </Header>
                </div>
            </div>

            <Divider fitted className="mt-2 custom-divider darken" />

            <FilterForm {...formProps} />

            <Divider fitted className="mt-2 custom-divider darken" />

            <Table selectable striped compact>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Funcionário</Table.HeaderCell>
                        <Table.HeaderCell>Horário</Table.HeaderCell>
                        <Table.HeaderCell>Evento</Table.HeaderCell>
                        <Table.HeaderCell>Página</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {loading && (
                        <Table.Row>
                            <Table.Cell colSpan={4}>Carregando dados...</Table.Cell>
                        </Table.Row>
                    )}
                    {logs?.dados?.length === 0 && !loading && (
                        <Table.Row>
                            <Table.Cell colSpan={4}>Sem dados...</Table.Cell>
                        </Table.Row>
                    )}
                    {!loading &&
                        logs?.dados?.map((log, idx) => (
                            <Table.Row key={idx}>
                                <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                                    {log.user.nome}
                                </Table.Cell>
                                <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                                    {DateTime.fromISO(log.logged_at).toFormat(
                                        'dd/MM/yyyy HH:mm:ss',
                                    )}
                                </Table.Cell>
                                <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                                    {formatEventToTable(log)}
                                </Table.Cell>
                                <Table.Cell>
                                    {log.tracking.action === 'PageLoaded'
                                        ? log.tracking.value
                                        : log.page}
                                </Table.Cell>
                            </Table.Row>
                        ))}
                </Table.Body>

                {logs?.dados?.length > 0 && (
                    <Table.Footer>
                        <Table.Row>
                            <Table.HeaderCell colSpan="2">
                                Página {logs?.pageNum}, exibindo{' '}
                                {logs?.dados?.length < 50 ? logs?.dados?.length : logs?.pageSize} de
                                {logs?.count}
                            </Table.HeaderCell>
                            <Table.HeaderCell colSpan="2">
                                {!Number.isNaN(pages) && pages > 0 && (
                                    <Menu floated="right" pagination>
                                        <Menu.Item
                                            disabled={logs?.pageNum === 1}
                                            icon="chevron left"
                                            onClick={() => onSubmit(search, logs?.pageNum - 1)}
                                        />
                                        {Array(pages)
                                            .fill(1)
                                            .map((_val, idx) => (
                                                <Menu.Item
                                                    key={idx}
                                                    as="a"
                                                    onClick={() => onSubmit(search, idx + 1)}
                                                >
                                                    {idx + 1}
                                                </Menu.Item>
                                            ))}
                                        <Menu.Item
                                            icon="chevron right"
                                            disabled={logs?.pageNum === pages}
                                            onClick={() => onSubmit(search, logs?.pageNum + 1)}
                                        />
                                    </Menu>
                                )}
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Footer>
                )}
            </Table>

            <AnimatedModal open={!!modalLog}>
                <Modal open={true} size="small" closeIcon onClose={() => setModalLog(null)}>
                    <Modal.Header>Dados enviados no formulário</Modal.Header>
                    <Modal.Content>
                        {modalLog &&
                            Object.keys(modalLog?.tracking?.form).map((field, idx) => (
                                <div key={idx} className="row">
                                    <div className="col-4">
                                        <strong>
                                            {Object.prototype.toString.call(
                                                modalLog?.tracking?.form[field],
                                            ) === '[object Object]'
                                                ? modalLog?.tracking?.form[field].label
                                                : field}
                                        </strong>
                                        :
                                    </div>
                                    <div className="col">
                                        {Object.prototype.toString.call(
                                            modalLog?.tracking?.form[field],
                                        ) === '[object Object]'
                                            ? Array.isArray(modalLog?.tracking?.form[field].value)
                                                ? modalLog?.tracking?.form[field].value.join(', ')
                                                : modalLog?.tracking?.form[field].value
                                            : modalLog?.tracking?.form[field]}
                                    </div>
                                </div>
                            ))}
                    </Modal.Content>
                </Modal>
            </AnimatedModal>
        </>
    );
}
